import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeftIcon, FloppyDiskIcon, PlusIcon, MagnifyingGlassIcon, XIcon,
  CheckCircleIcon, WarningCircleIcon, XCircleIcon, InfoIcon, DownloadSimpleIcon,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  getFormulation, createFormulation, updateFormulation, listIngredients,
  formulationPdfUrl, downloadExport,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const REGIONS = ["European Union", "United States", "United Kingdom", "ASEAN", "Japan", "South Korea", "China", "India", "Canada", "Australia"];

const SEV = {
  error:   { color: "var(--danger)",  Icon: XCircleIcon,      label: "ERROR" },
  warning: { color: "#B38600",        Icon: WarningCircleIcon, label: "WARNING" },
  info:    { color: "var(--text-muted)", Icon: InfoIcon,      label: "INFO" },
};

function IngredientPicker({ onPick }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      listIngredients(q ? { q } : {})
        .then((d) => setItems(d.slice(0, 20)))
        .finally(() => setLoading(false));
    }, 150);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="border border-[var(--border)]" data-testid="ingredient-picker">
      <div className="px-3 py-2 border-b border-[var(--border)] flex items-center gap-2">
        <MagnifyingGlassIcon size={14} className="text-[var(--text-muted)]" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ingredients to add…" className="rounded-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-8 px-0 font-mono text-sm" data-testid="picker-search" />
      </div>
      <div className="max-h-64 overflow-auto">
        {loading ? (
          <div className="p-4 text-center font-mono text-[10px] text-[var(--text-muted)]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-4 text-center font-mono text-[10px] text-[var(--text-muted)]">No matches</div>
        ) : items.map((ing) => (
          <button
            type="button"
            key={ing.id}
            onClick={() => onPick(ing)}
            className="w-full text-left px-3 py-2 cell-border last:border-b-0 hover:bg-[var(--surface)] flex items-center justify-between gap-4"
            data-testid={`picker-row-${ing.id}`}
          >
            <div className="min-w-0">
              <div className="font-heading font-medium tracking-tight truncate">{ing.ingredientName}</div>
              <div className="font-mono text-[10px] text-[var(--text-muted)] truncate">
                {ing.inciEU || "—"} {ing.casNumber ? `· CAS ${ing.casNumber}` : ""}
              </div>
            </div>
            <PlusIcon size={14} className="text-[var(--primary)]" />
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FormulationEdit({ mode }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [form, setForm] = useState({ name: "", description: "", region: "European Union", ingredients: [] });
  const [preview, setPreview] = useState({ total_percent: 0, flags: [], summary: { errors: 0, warnings: 0, infos: 0 } });
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const debounce = useRef(null);

  useEffect(() => {
    if (mode === "edit" && id) {
      getFormulation(id).then((d) => {
        setForm({ name: d.name, description: d.description || "", region: d.region || "European Union", ingredients: d.ingredients || [] });
        setPreview({ total_percent: d.total_percent || 0, flags: d.flags || [], summary: d.summary || {} });
      }).catch(() => toast.error("Formulation not found"));
    }
  }, [mode, id]);

  // Recompute preview on form change (debounced)
  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      if (mode === "edit" && id) {
        setBusy(true);
        try {
          const saved = await updateFormulation(id, form);
          setPreview({ total_percent: saved.total_percent, flags: saved.flags, summary: saved.summary });
        } catch (e) { /* ignore */ } finally { setBusy(false); }
      } else {
        // local compute for create (sum only)
        const total = (form.ingredients || []).reduce((a, b) => a + (Number(b.percent) || 0), 0);
        setPreview({ total_percent: total, flags: [], summary: {} });
      }
    }, 450);
    return () => debounce.current && clearTimeout(debounce.current);
  // eslint-disable-next-line
  }, [form.ingredients, form.region]);

  const addIngredient = (ing) => {
    if (form.ingredients.some((r) => r.ingredient_id === ing.id)) {
      toast.info(`${ing.ingredientName} already in this formulation`); return;
    }
    setForm((f) => ({
      ...f,
      ingredients: [...f.ingredients, {
        ingredient_id: ing.id,
        ingredient_name: ing.ingredientName,
        inci_eu: ing.inciEU || "",
        cas_number: ing.casNumber || "",
        percent: 0,
      }],
    }));
  };

  const setPercent = (idx, value) => {
    setForm((f) => {
      const next = [...f.ingredients];
      next[idx] = { ...next[idx], percent: Number(value) || 0 };
      return { ...f, ingredients: next };
    });
  };

  const removeRow = (idx) => setForm((f) => ({ ...f, ingredients: f.ingredients.filter((_, i) => i !== idx) }));

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (mode === "edit") {
        const saved = await updateFormulation(id, form);
        setPreview({ total_percent: saved.total_percent, flags: saved.flags, summary: saved.summary });
        toast.success("Formulation saved");
      } else {
        const created = await createFormulation(form);
        toast.success("Formulation created");
        navigate(`/formulations/${created.id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const flagsByIngredient = useMemo(() => {
    const m = {};
    for (const f of (preview.flags || [])) {
      if (f.ingredient_id) {
        (m[f.ingredient_id] ||= []).push(f);
      }
    }
    return m;
  }, [preview.flags]);

  const globalFlags = (preview.flags || []).filter((f) => !f.ingredient_id);

  return (
    <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 fade-in" data-testid="formulation-edit-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-[var(--text-muted)] hover:text-[var(--text)]" data-testid="back-btn">
          <ArrowLeftIcon size={14} /> Back
        </button>
        <div className="flex items-center gap-2">
          {mode === "edit" && (
            <Button variant="outline" onClick={() => downloadExport(formulationPdfUrl(id), `${form.name || "formulation"}.pdf`)} className="rounded-none h-9 font-mono text-[11px] uppercase tracking-[0.18em]" data-testid="export-pdf-btn">
              <DownloadSimpleIcon size={14} className="mr-2" /> PDF
            </Button>
          )}
          {hasRole("editor") && (
            <Button onClick={handleSave} disabled={saving} className="btn-primary rounded-none h-9 font-mono text-[11px] uppercase tracking-[0.18em]" data-testid="save-formulation-btn">
              <FloppyDiskIcon size={14} className="mr-2" /> {saving ? "Saving…" : (mode === "edit" ? "Save" : "Create")}
            </Button>
          )}
        </div>
      </div>

      <section className="border border-[var(--border)] p-6 bg-white">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className="label-kicker">Formulation Name *</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none h-10 font-heading text-xl mt-2" placeholder="e.g. Hydration Serum v3" data-testid="inp-name" />
          </div>
          <div>
            <label className="label-kicker">Target Region</label>
            <Select value={form.region} onValueChange={(v) => setForm({ ...form, region: v })}>
              <SelectTrigger className="rounded-none h-10 font-mono text-sm mt-2" data-testid="inp-region"><SelectValue /></SelectTrigger>
              <SelectContent>
                {REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3">
            <label className="label-kicker">Description</label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="rounded-none text-sm mt-2" placeholder="Internal notes, product type, targeted claims…" data-testid="inp-description" />
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-6">
        <div className="lg:col-span-8 border border-[var(--border)] bg-white">
          <div className="px-4 py-3 border-b border-[var(--border)] flex items-center justify-between">
            <div className="label-kicker">Ingredients ({form.ingredients.length})</div>
            <div className="font-mono text-xs" data-testid="total-percent">
              TOTAL · <b className={preview.total_percent > 100 ? "text-[var(--danger)]" : ""}>{(preview.total_percent || 0).toFixed(2)}%</b>
              {busy && <span className="ml-2 text-[var(--text-muted)]">computing…</span>}
            </div>
          </div>

          {form.ingredients.length === 0 ? (
            <div className="p-12 text-center" data-testid="ingredients-empty">
              <div className="font-mono text-xs text-[var(--text-muted)]">No ingredients yet. Use the picker on the right to add some.</div>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr>
                  {["INGREDIENT", "INCI (EU)", "CAS", "TARGET %", "STATUS", ""].map((h, i) => (
                    <th key={i} className="label-kicker text-left px-4 py-2 border-b border-[var(--border)] bg-[var(--surface)]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {form.ingredients.map((row, idx) => {
                  const rowFlags = flagsByIngredient[row.ingredient_id] || [];
                  const worst = rowFlags.find((f) => f.severity === "error")
                    || rowFlags.find((f) => f.severity === "warning")
                    || rowFlags[0];
                  const cfg = worst ? SEV[worst.severity] : null;
                  return (
                    <tr key={row.ingredient_id} className="cell-border align-top" data-testid={`form-ing-row-${idx}`}>
                      <td className="px-4 py-3 font-heading font-medium tracking-tight">{row.ingredient_name}</td>
                      <td className="px-4 py-3 font-mono text-xs">{row.inci_eu || "—"}</td>
                      <td className="px-4 py-3 font-mono text-xs">{row.cas_number || "—"}</td>
                      <td className="px-4 py-3">
                        <Input type="number" step="0.01" min="0" value={row.percent} onChange={(e) => setPercent(idx, e.target.value)} className="rounded-none h-8 w-24 font-mono text-sm" data-testid={`form-ing-pct-${idx}`} />
                      </td>
                      <td className="px-4 py-3">
                        {cfg ? (
                          <div className="flex items-start gap-1.5 max-w-[220px]">
                            <cfg.Icon size={14} weight="fill" style={{ color: cfg.color }} className="shrink-0 mt-0.5" />
                            <span className="font-mono text-[11px]" style={{ color: cfg.color }}>{worst.message}</span>
                          </div>
                        ) : (
                          <span className="font-mono text-[11px] text-[var(--text-muted)]">OK</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => removeRow(idx)} className="text-[var(--text-muted)] hover:text-[var(--danger)]" data-testid={`form-ing-remove-${idx}`}>
                          <XIcon size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="lg:col-span-4 space-y-4">
          <div>
            <div className="label-kicker mb-2">Add Ingredient</div>
            <IngredientPicker onPick={addIngredient} />
          </div>

          <div className="border border-[var(--border)] bg-white" data-testid="compliance-panel">
            <div className="px-4 py-3 border-b border-[var(--border)] flex items-center justify-between">
              <div className="label-kicker">Compliance</div>
              <div className="flex items-center gap-3 font-mono text-[11px]">
                <span className="text-[var(--danger)]">{preview.summary?.errors || 0} E</span>
                <span style={{ color: "#B38600" }}>{preview.summary?.warnings || 0} W</span>
                <span className="text-[var(--text-muted)]">{preview.summary?.infos || 0} I</span>
              </div>
            </div>
            <div className="p-4 space-y-3">
              {mode !== "edit" && (
                <div className="font-mono text-[11px] text-[var(--text-muted)]">Compliance runs after the formulation is saved.</div>
              )}
              {mode === "edit" && (preview.flags || []).length === 0 && (
                <div className="flex items-center gap-2 font-mono text-xs" style={{ color: "var(--success)" }} data-testid="all-clear">
                  <CheckCircleIcon size={14} weight="fill" /> All clear for {form.region}.
                </div>
              )}
              {globalFlags.map((f, i) => {
                const cfg = SEV[f.severity] || SEV.info;
                return (
                  <div key={i} className="flex items-start gap-2" data-testid={`global-flag-${i}`}>
                    <cfg.Icon size={14} weight="fill" style={{ color: cfg.color }} className="shrink-0 mt-0.5" />
                    <div className="font-mono text-[11px]" style={{ color: cfg.color }}>
                      <b>{cfg.label}</b> — {f.message}
                    </div>
                  </div>
                );
              })}
              {Object.entries(flagsByIngredient).map(([iid, flags]) => (
                <div key={iid}>
                  <div className="label-kicker mb-1">{flags[0].ingredient_name}</div>
                  {flags.map((f, i) => {
                    const cfg = SEV[f.severity] || SEV.info;
                    return (
                      <div key={i} className="flex items-start gap-2 pl-1">
                        <cfg.Icon size={12} weight="fill" style={{ color: cfg.color }} className="shrink-0 mt-0.5" />
                        <div className="font-mono text-[11px]" style={{ color: cfg.color }}>{f.message}</div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
